import { createSlice } from '@reduxjs/toolkit';
import lodashFindIndex from 'lodash/findIndex';
import lodashIsEmpty from 'lodash/isEmpty';

import constants from '../../Config/constants';
import CartHelper from '../../Helper/Cart';
import { assignAllowedKeys } from '../../Helper/Reducer';

import { checkout, mealPlan } from '../defaultValues';

const getDayAndItemIndex = ({ cart, day, item }) => {
  const dayIndex = lodashFindIndex(cart, { day }); // check for cart day index
  const dayItemIndex = lodashFindIndex(
    cart[dayIndex]?.items || [],
    CartHelper.getConditionForItemVariant(item),
  ); // check for cart item index
  return { dayIndex, dayItemIndex };
};

export const mealPlanSlice = createSlice({
  name: 'mealPlan',
  initialState: {
    data: mealPlan.initialValue,
    hint: mealPlan.hint,
  },
  reducers: {
    addMealPlanItem: (state, action) => {
      const { day, item } = action.payload; // day = number, item = object
      // add default values to reuse existing helper
      item.item_id = item.id;
      item.extras = item.extras || [];
      const { dayIndex, dayItemIndex } = getDayAndItemIndex({
        cart: state.data.cart,
        day,
        item,
      });
      if (dayIndex === -1) {
        // 1. push as first item on this day
        state.data.cart.push({ day, items: [item] });
      } else {
        if (dayItemIndex === -1) {
          // 2. push the new item for this day
          state.data.cart[dayIndex].items.push(item);
        } else {
          // 3. add the new quantity to existing item on this day quantity
          state.data.cart[dayIndex].items[dayItemIndex].quantity +=
            item.quantity;
        }
      }
    },
    removeMealPlanDaysItems: (state, action) => {
      // action.payload is number[] with values from weekday indexes (1=Mon, 5=Fri)
      const dayIndexes = action.payload;
      for (let i = 0; i <= dayIndexes.length; i++) {
        const dayInCartIndex = lodashFindIndex(state.data.cart, {
          day: dayIndexes[i],
        });
        const isDayHasItems = !lodashIsEmpty(state.data.cart[dayInCartIndex]);
        if (isDayHasItems) {
          state.data.cart.splice(dayInCartIndex, 1); // remove day cart
        }
      }
    },
    resetMealPlan: (state) => {
      state.data = mealPlan.initialValue; // reset meal plan journey
      state.hint = mealPlan.hint; // reset meal plan hint
    },
    setMealPlanAddressSelection: (state, action) => {
      state.data.checkoutFlag = action.payload;
    },
    setMealPlanCheckingOut: (state, action) => {
      state.data.isCheckingOut = action.payload; // boolean
    },
    setMealPlanCheckoutData: (state, action) => {
      const { key, data } = action.payload;
      state.data.checkoutData[key] = data;
      if (key === checkout.keys.DELIVERY_ADDRESS && !constants.isWeb) {
        state.data.checkoutFlag = false;
      }
    },
    setMealPlanDay: (state, action) => {
      state.data.day = action.payload;
    },
    setMealPlanHint: (state, action) => {
      assignAllowedKeys(state.hint, action.payload, Object.keys(state.hint));
    },
    setMealPlanType: (state, action) => {
      state.data.type = action.payload;
    },
    updateMealPlanItemQuantity: (state, action) => {
      const { day, data, quantity } = action.payload; // day = number, data = object, quantity = number
      const { dayIndex, dayItemIndex } = getDayAndItemIndex({
        cart: state.data.cart,
        day,
        item: data,
      });
      if (quantity <= 0) {
        // remove item if quantity is 0 or less
        state.data.cart[dayIndex].items.splice(dayItemIndex, 1); // remove item for this day
      } else {
        state.data.cart[dayIndex].items[dayItemIndex].quantity = quantity; // update to new quantity
      }
    },
  },
});

export const {
  addMealPlanItem,
  removeMealPlanDaysItems,
  resetMealPlan,
  setMealPlanAddressSelection,
  setMealPlanCheckingOut,
  setMealPlanCheckoutData,
  setMealPlanDay,
  setMealPlanHint,
  setMealPlanType,
  updateMealPlanItemQuantity,
} = mealPlanSlice.actions;
export default mealPlanSlice.reducer;
